import * as Sentry from '@sentry/nextjs'
import type { User } from 'firebase/auth'
import React, { createContext, useCallback, useContext, useState } from 'react'

import { getReferrerRewards, getReferrerSubscriptions } from '@/lib/referral-service'

import type { FetchBenefitsResponse, ReferralSubscriptionDto } from '@/clients/referral-service'

export type RedeemConfirmPayload = {
  code: string
  subscriptionId: string
  productType: string
  voucherAmount: number
  promoCodeId: string
  renewalDate?: string
  originalPrice?: number
  benefitType?: string
}

type RewardsContextValue = {
  rewards: FetchBenefitsResponse | null
  subscriptions: ReferralSubscriptionDto[]
  loadingRewards: boolean
  loadingSubscriptions: boolean
  errorRewards: string | null
  errorSubscriptions: string | null
  confirmPayload: RedeemConfirmPayload | null
  fetchRewards: (user: User | null) => Promise<void>
  fetchSubscriptions: (user: User | null, referrerUsername: string) => Promise<void>
  setConfirmPayload: (payload: RedeemConfirmPayload | null) => void
  clearConfirmPayload: () => void
}

const RewardsContext = createContext<RewardsContextValue | undefined>(undefined)

export const RewardsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [rewards, setRewards] = useState<FetchBenefitsResponse | null>(null)
  const [subscriptions, setSubscriptions] = useState<ReferralSubscriptionDto[]>([])
  const [loadingRewards, setLoadingRewards] = useState(false)
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false)
  const [errorRewards, setErrorRewards] = useState<string | null>(null)
  const [errorSubscriptions, setErrorSubscriptions] = useState<string | null>(null)
  const [confirmPayload, setConfirmPayloadState] = useState<RedeemConfirmPayload | null>(null)

  const fetchRewards = useCallback(async (user: User | null) => {
    if (!user) {
      setRewards(null)
      return
    }
    setLoadingRewards(true)
    setErrorRewards(null)
    try {
      const data = await getReferrerRewards(user)
      setRewards(data ?? null)
      if (!data) {
        setErrorRewards('Unable to fetch your rewards at the moment.')
      }
    } catch (e) {
      Sentry.captureException(e)
      setErrorRewards('Unable to fetch your rewards at the moment.')
      setRewards(null)
    } finally {
      setLoadingRewards(false)
    }
  }, [])

  const fetchSubscriptions = useCallback(async (user: User | null, referrerUsername: string) => {
    if (!user || !referrerUsername) {
      setSubscriptions([])
      return
    }
    setLoadingSubscriptions(true)
    setErrorSubscriptions(null)
    try {
      const data = await getReferrerSubscriptions(user, referrerUsername)
      setSubscriptions(data?.subscriptions ? Array.from(data.subscriptions) : [])
      if (!data?.subscriptions) {
        setSubscriptions([])
      }
    } catch (e) {
      Sentry.captureException(e)
      setErrorSubscriptions('Unable to load subscriptions.')
      setSubscriptions([])
    } finally {
      setLoadingSubscriptions(false)
    }
  }, [])

  const setConfirmPayload = useCallback((payload: RedeemConfirmPayload | null) => {
    setConfirmPayloadState(payload)
  }, [])

  const clearConfirmPayload = useCallback(() => {
    setConfirmPayloadState(null)
  }, [])

  const value: RewardsContextValue = {
    rewards,
    subscriptions,
    loadingRewards,
    loadingSubscriptions,
    errorRewards,
    errorSubscriptions,
    confirmPayload,
    fetchRewards,
    fetchSubscriptions,
    setConfirmPayload,
    clearConfirmPayload,
  }

  return <RewardsContext.Provider value={value}>{children}</RewardsContext.Provider>
}

export const useRewardsContext = (): RewardsContextValue => {
  const context = useContext(RewardsContext)
  if (context === undefined) {
    throw new Error('useRewardsContext must be used within a RewardsProvider')
  }
  return context
}
